'use client'

import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ThankYouMark } from '@/components/forms/ThankYouMark'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { getClientSideURL } from '@/utilities/getURL'

interface AskQuestionButtonProps {
  formId: number | null
  staffName: string
  staffEmail: string | null
  staffMobile: string | null
  staffPhotoUrl: string | null
}

/**
 * "Ask A Question" outline button + centered modal per Figma 1:15142 / 1:15554.
 *
 * Submits via the Payload form-builder plugin endpoint (/api/form-submissions),
 * following the same pattern as PropertyQuestionForm — the staff member's
 * name/email are appended to the submission data so the office knows who the
 * question is for.
 */
export function AskQuestionButton({
  formId,
  staffName,
  staffEmail,
  staffMobile,
  staffPhotoUrl,
}: AskQuestionButtonProps) {
  const [open, setOpen] = useState(false)

  if (!formId) return null

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        Ask A Question
      </Button>
      {open && (
        <AskQuestionModal
          formId={formId}
          staffName={staffName}
          staffEmail={staffEmail}
          staffMobile={staffMobile}
          staffPhotoUrl={staffPhotoUrl}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2 w-full">
      {/* Canonical uppercase Body/bodyXs field label — same as EnquiryModal / Payload forms */}
      <Label asChild>
        <span>{label}</span>
      </Label>
      {children}
    </div>
  )
}

function AskQuestionModal({
  formId,
  staffName,
  staffEmail,
  staffMobile,
  staffPhotoUrl,
  onClose,
}: AskQuestionButtonProps & { formId: number; onClose: () => void }) {
  const [isLoading, setIsLoading] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Close on Escape + lock body scroll while open
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [onClose])

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      setError(null)
      setIsLoading(true)

      const formData = new FormData(e.currentTarget)
      // Field names match the Payload "Contact Form" form-builder fields
      const submissionData: { field: string; value: string }[] = [
        { field: 'first-name', value: String(formData.get('first-name') ?? '') },
        { field: 'last-name', value: String(formData.get('last-name') ?? '') },
        { field: 'email', value: String(formData.get('email') ?? '') },
        { field: 'mobile', value: String(formData.get('mobile') ?? '') },
        { field: 'message', value: String(formData.get('message') ?? '') },
        // Staff context (same pattern as PropertyQuestionForm's hidden fields)
        { field: 'staffName', value: staffName },
        { field: 'staffEmail', value: staffEmail || '' },
      ]

      try {
        const req = await fetch(`${getClientSideURL()}/api/form-submissions`, {
          body: JSON.stringify({ form: formId, submissionData }),
          headers: { 'Content-Type': 'application/json' },
          method: 'POST',
        })
        const res = await req.json()

        if (req.status >= 400) {
          setError(res.errors?.[0]?.message || 'Something went wrong. Please try again.')
          setIsLoading(false)
          return
        }

        setHasSubmitted(true)
        setIsLoading(false)
      } catch {
        setError('Something went wrong. Please try again.')
        setIsLoading(false)
      }
    },
    [formId, staffName, staffEmail],
  )

  // Portal to <body> so the overlay paints above the fixed site header (z-40)
  // regardless of any ancestor stacking contexts.
  return createPortal(
    <div
      className="bg-background/80 fixed inset-0 z-[100] flex items-center justify-center p-0 backdrop-blur-lg sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Ask A Question"
    >
      {/* Dimmed overlay */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-default"
      />

      {/* Modal panel — EnquiryModal chrome: theme/background, square corners,
          full-screen sheet on mobile, centred 400px card from sm up (staff modal
          Figma 1:15554 is 400 wide vs the property modal's 368). */}
      {/* Asana 021 — same 1px theme/border as the property enquiry card. */}
      <div className="relative flex h-full max-h-none w-full max-w-none flex-col gap-8 overflow-y-auto bg-background p-6 sm:h-auto sm:max-h-[90vh] sm:w-[400px] sm:max-w-[400px] sm:rounded-sm sm:border sm:border-border sm:shadow-md">
        {/* Header row — title binds Headings/headlineSmAlt: Merriweather ITALIC 24/32 */}
        <div className="flex items-start gap-1">
          <h3 className="flex-1 font-display text-display-lg font-normal italic text-foreground">
            Ask A Question
          </h3>
          <Button
            type="button"
            variant="outline"
            size="icon-mini"
            onClick={onClose}
            aria-label="Close dialog"
            className="shrink-0"
          >
            <X className="size-3" />
          </Button>
        </div>

        <div className="flex flex-col gap-8">
          {/* Staff card — Figma: 85px photo, gap 24px; name 18/24, mobile 14/16 */}
          <div className="flex items-center gap-6">
            {staffPhotoUrl && (
              <img
                src={staffPhotoUrl}
                alt={staffName}
                className="size-[85px] object-cover shrink-0"
              />
            )}
            <div className="flex flex-col gap-2 min-w-0">
              <p className="text-body font-normal text-foreground">{staffName}</p>
              {staffMobile && (
                <p className="text-body-sm font-light text-foreground">{staffMobile}</p>
              )}
            </div>
          </div>

          {hasSubmitted ? (
            <div className="flex flex-col items-start gap-4">
              {/* Asana 018 — shared thank-you mark on all form success states */}
              <ThankYouMark />
              <p className="font-display text-display-lg font-normal text-foreground">
                Thank you — your question has been sent.
              </p>
              <p className="text-body-sm font-light text-foreground">
                {staffName.split(' ')[0]} will be in touch with you shortly.
              </p>
              <Button onClick={onClose} className="self-start">
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-8">
              <div className="flex flex-col gap-6">
                <p className="text-body-md font-normal text-foreground">
                  Please confirm your details.
                </p>
                <Field label="First Name *">
                  <Input
                    name="first-name"
                    required
                    placeholder="e.g. Alex"
                    autoComplete="given-name"
                  />
                </Field>
                <Field label="Last Name *">
                  <Input
                    name="last-name"
                    required
                    placeholder="e.g. Smith"
                    autoComplete="family-name"
                  />
                </Field>
                <Field label="Email *">
                  <Input
                    name="email"
                    type="email"
                    required
                    placeholder="your@email.com"
                    autoComplete="email"
                  />
                </Field>
                <Field label="Mobile *">
                  <Input
                    name="mobile"
                    type="tel"
                    required
                    placeholder="04XX XXX XXX"
                    autoComplete="tel"
                  />
                </Field>
              </div>

              <div className="flex flex-col gap-6">
                <p className="text-body-md font-normal text-foreground">
                  What would you like to ask?
                </p>
                <Field label="Your Message *">
                  <Textarea
                    name="message"
                    required
                    placeholder="Type your message here…"
                    className="h-16"
                  />
                </Field>
              </div>

              {error && <p className="text-body-sm text-destructive">{error}</p>}

              <Button type="submit" disabled={isLoading} className="self-start">
                {isLoading ? 'Sending…' : 'Send'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
